import type { IBooking } from "@/services/types";
import { BookingType } from "@/services/types";

/**
 * Calculate total hours from an array of objects with hours property
 * Generic utility that works with any object containing a hours property
 * @param items Array of items with hours property
 * @returns Total hours sum
 */
export function sumHours(items: { hours: number }[]): number {
  return items.reduce((sum, item) => sum + item.hours, 0);
}

/**
 * Calculate total hours from an array of bookings
 * @param bookings Array of bookings
 * @returns Total hours sum
 */
export function calculateTotalHours(bookings: IBooking[]): number {
  return bookings.reduce((sum, booking) => sum + (booking.hours || 0), 0);
}

/**
 * Calculate hours from project bookings only (excludes absence)
 * @param bookings Array of bookings
 * @returns Total project hours (non-absence)
 */
export function calculateProjectHours(bookings: IBooking[]): number {
  return bookings
    .filter((booking) => booking.b_type !== BookingType.ABSENCE)
    .reduce((sum, booking) => sum + (booking.hours || 0), 0);
}

/**
 * Calculate hours from absence bookings only
 * @param bookings Array of bookings
 * @returns Total absence hours
 */
export function calculateAbsenceHours(bookings: IBooking[]): number {
  return bookings
    .filter((booking) => booking.b_type === BookingType.ABSENCE)
    .reduce((sum, booking) => sum + (booking.hours || 0), 0);
}

/**
 * Calculate hours from hard bookings only (excludes soft bookings and absence)
 * Used for overbooking calculations - soft bookings should NOT count toward daily limit
 * @param bookings Array of bookings
 * @returns Total hard booking hours
 */
export function calculateHardBookingHours(bookings: IBooking[]): number {
  return bookings
    .filter((booking) => booking.b_type === BookingType.HARD_BOOKING)
    .reduce((sum, booking) => sum + (booking.hours || 0), 0);
}

/**
 * Calculate hours from soft bookings only
 * @param bookings Array of bookings
 * @returns Total soft booking hours
 */
export function calculateSoftBookingHours(bookings: IBooking[]): number {
  return bookings
    .filter((booking) => booking.b_type === BookingType.SOFT_BOOKING)
    .reduce((sum, booking) => sum + (booking.hours || 0), 0);
}

/**
 * Check if bookings contain only soft bookings (no hard bookings)
 * Used to determine if overbooking logic should be skipped
 * @param bookings Array of bookings
 * @returns True if all non-absence bookings are soft bookings
 */
export function isSoftBookingOnly(bookings: IBooking[]): boolean {
  const nonAbsenceBookings = bookings.filter(
    (b) => b.b_type !== BookingType.ABSENCE,
  );
  if (nonAbsenceBookings.length === 0) return false;
  return nonAbsenceBookings.every((b) => b.b_type === BookingType.SOFT_BOOKING);
}

/**
 * Calculate average hours per booking
 * @param bookings Array of bookings
 * @returns Average hours (rounded to 1 decimal place)
 */
export function calculateAverageHours(bookings: IBooking[]): number {
  if (bookings.length === 0) return 0;
  const total = calculateTotalHours(bookings);
  return Math.round((total / bookings.length) * 10) / 10;
}

/**
 * Calculate total hours excluding specific booking IDs
 * @param bookings Array of bookings
 * @param excludeIds Array of booking IDs to exclude
 * @returns Total hours excluding specified bookings
 */
export function calculateHoursExcluding(
  bookings: IBooking[],
  excludeIds: number[],
): number {
  return bookings
    .filter((booking) => !excludeIds.includes(booking.id))
    .reduce((sum, booking) => sum + (booking.hours || 0), 0);
}

/**
 * Booking statistics result
 */
export interface BookingStats {
  total: number;
  project: number;
  absence: number;
  average: number;
  isOverbooked: boolean;
  isFullyBooked: boolean;
}

/**
 * Calculate comprehensive booking statistics
 * @param bookings Array of bookings
 * @param dayHours Expected hours per day (default 8)
 * @returns Comprehensive statistics object
 */
export function calculateBookingStats(
  bookings: IBooking[],
  dayHours = 8,
): BookingStats {
  const total = calculateTotalHours(bookings);
  const project = calculateProjectHours(bookings);
  const absence = calculateAbsenceHours(bookings);
  const average = calculateAverageHours(bookings);

  // Consider "fully booked" with 0.5 hour padding
  const fullyBookedThreshold = dayHours - 0.5;

  return {
    total,
    project,
    absence,
    average,
    isOverbooked: total > dayHours,
    isFullyBooked: total >= fullyBookedThreshold,
  };
}
